/**
 * Daytime palette, matched to the Capital Rift reference captures.
 *
 * The world renders as a bright toy city in full daylight: saturated grass,
 * mid-grey roads with painted lanes, and buildings in a spread of warm facade
 * colours with dark roofs. The chrome above it stays dark and translucent, so
 * ownership reads through labels and soft parcel overlays rather than glow.
 */

/** Sky and distance haze. The camera sees sky only at the lowest tilts. */
export const SKY = 0xb9cdd9;
export const HAZE = 0xc7d4bd;
/** What the sky fades to at planet-framing distances. */
export const SPACE = 0x101c2c;

/** The far-zoom world map: ocean under Natural Earth land. */
export const OCEAN = 0x2f7bb5;
export const OVERVIEW_ROAD = 0x4c4438;
/** State/county borders on the detailed map layer — quieter than country. */
export const ADMIN1_BORDER = 0x55684a;

/** Resources overlay: stone/"concrete" deposits (ore uses ORE_HIGH). */
export const CONCRETE = 0x4f7fd0;

/** Foot-traffic overlay: quiet streets stay dim brick, busy ones glow amber;
 * the ground tints red (quiet) → green (packed with customers). */
export const TRAFFIC_ROAD_QUIET = 0x8a4a3a;
export const TRAFFIC_ROAD_GLOW = 0xffb52e;
export const TRAFFIC_GROUND_QUIET = 0xb8453a;
export const TRAFFIC_GROUND_BUSY = 0x3fae62;

/** Grass. The ground plane carries a blotchy texture built from these. */
export const GRASS_BASE = 0x7ca757;
export const GRASS_LIGHT = 0x92c065;
export const GRASS_DARK = 0x69934b;

/** Parcel plots: hash-varied grass, with the cadastre drawn as thin bright
 * lines on every lot like the reference. */
export const PLOT_LINE = 0x9fe8a8;

/** Road surface by class, with pale sidewalk casing and painted dashes. */
export const ROAD_SURFACE: Record<string, number> = {
  motorway: 0x53575d,
  primary: 0x555960,
  secondary: 0x575b61,
  residential: 0x5a5e64,
  service: 0x63666c,
};
export const ROAD_CASING = 0xb0b0a8;
export const LANE_PAINT = 0xe9e9df;

/**
 * Facade colours, picked per building by id hash. Windows come from a single
 * shared grid texture that these tints multiply.
 */
export const FACADES: readonly number[] = [
  0xe7dfc8, // cream
  0xd9c9a8, // beige
  0xb05a40, // brick
  0x9e4a38, // red brick
  0x8f6b4c, // brown
  0xc79a93, // dusty pink
  0xcbb894, // tan
  0xb9bcb4, // grey render
  0xa9a06b, // olive
];
export const FACADE_TOWERS: readonly number[] = [0xd7d3c6, 0xc4c8cc, 0xcfc6b2];
export const WALL_INDUSTRIAL = 0x4d5158;

export const ROOF_FLAT = 0x3d4146;
export const ROOF_INDUSTRIAL = 0x32353a;
/** Pitched roofs on small houses, hash-picked. */
export const ROOF_PITCHED: readonly number[] = [0x4a4038, 0x53453a, 0x8a4636, 0x474b50];

/** Building shadows, projected onto the ground as translucent geometry. */
export const SHADOW = 0x1c2a1a;
export const SHADOW_OPACITY = 0.17;

/** Ownership accents — the same accents the chrome uses. */
export const ACCENT_MINE = 0xf5c04a;
export const ACCENT_THEIRS = 0xdde5ea;
export const ACCENT_LISTING = 0x7fe7a8;
export const ACCENT_SELECTED = 0xffffff;

/** Overlay ramps (foot traffic cold→hot, resources soil→ore). */
export const HEAT_COLD = 0x59a7d8;
export const HEAT_HOT = 0xff5a3c;
export const ORE_LOW = 0x67793f;
export const ORE_HIGH = 0xe07b3e;

/** Logistics lanes. */
export const LANE_LINE = 0x2b6a8c;
export const LANE_COURIER = 0x2fa7e0;

/** Cars ambling the roads. Mostly sober paint, one loud one. */
export const CAR_COLORS: readonly number[] = [0xc94f44, 0x4a6fb5, 0xd8d8d2, 0x50555c, 0x8fb06a];

/** Pedestrians: everyday clothing colours under one shared beige head. */
export const PED_BODIES: readonly number[] = [
  0x7a5a40, 0x8a6a4e, 0x5c5148, 0x4a6fb5, 0x506050, 0x94897a, 0x6e4a4a, 0x3f4a58,
];
export const PED_HEAD = 0xd9c9a8;

/** Trees dotted over open parcels. */
export const TREE_CANOPY = 0x4f7d3c;
export const TREE_TRUNK = 0x5d4a33;

/** Floor-plan rooms: green fills, cream partition walls with white caps. */
export const ROOM_SELLABLE = 0x5cba7d;
export const ROOM_SELECTED = 0x8ef0b4;
export const ROOM_COMMON = 0x8fa392;
export const ROOM_MINE = 0xe8c964;
export const ROOM_TAKEN = 0x9fb4b0;
export const ROOM_LINE = 0xeafff2;
export const ROOM_DASH = 0xd8ffe4;
export const PARTITION_WALL = 0xe4ddcd;
export const PARTITION_CAP = 0xf7f5ef;

/** Sun shading for baked wall colours: ambient floor plus facing boost. */
export const SUN_AMBIENT = 0.78;
export const SUN_DIRECT = 0.22;
/** Faint ground-contact AO at the wall base; day walls stay mostly even. */
export const WALL_BASE_SHADE = 0.9;

/** Blend two packed RGB colours. */
export function mixRgb(a: number, b: number, t: number): number {
  const k = Math.min(1, Math.max(0, t));
  const ar = (a >> 16) & 0xff;
  const ag = (a >> 8) & 0xff;
  const ab = a & 0xff;
  const br = (b >> 16) & 0xff;
  const bg = (b >> 8) & 0xff;
  const bb = b & 0xff;
  return (
    (Math.round(ar + (br - ar) * k) << 16) |
    (Math.round(ag + (bg - ag) * k) << 8) |
    Math.round(ab + (bb - ab) * k)
  );
}

/** Scale a packed RGB colour towards black (k<1) or white-clip (k>1). */
export function shade(rgb: number, k: number): number {
  const r = Math.min(255, Math.round(((rgb >> 16) & 0xff) * k));
  const g = Math.min(255, Math.round(((rgb >> 8) & 0xff) * k));
  const b = Math.min(255, Math.round((rgb & 0xff) * k));
  return (r << 16) | (g << 8) | b;
}

/** Small deterministic hash for stable per-feature variation. */
export function hash01(id: string): number {
  let h = 2166136261;
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return ((h >>> 0) % 10_000) / 10_000;
}

/** Pick from a list by id hash, with an optional salt for independent picks. */
export function hashPick<T>(list: readonly T[], id: string, salt = ""): T {
  return list[Math.floor(hash01(id + salt) * list.length) % list.length]!;
}
